import { MinusIcon, PlusIcon, StarFourIcon } from '@phosphor-icons/react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetSettingsByToken } from '@/api/market-shop/client/get-settings-by-token'
import { Button } from '@/components/business/market-shop/button'
import { Chip } from '@/components/business/market-shop/chip'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'
import { popupFactory } from '@/factories'
import { MarketShopProductModel } from '@/models'
import { getMarketShopImageHref } from '@/pages/market-shop/market-shop.utils'
import { ProductDetails } from '@/pages/market-shop/product-details'
import { formatPrice } from '@/utils'

import * as S from './product-card.styled'

export interface ProductCardProps {
  product: MarketShopProductModel
  quantity: number
  onAddToCart: (product: MarketShopProductModel) => void
  onSubtractFromCart: (product: MarketShopProductModel) => void
  hideControls: boolean
}

export const ProductCard = ({
  product,
  quantity,
  onAddToCart,
  onSubtractFromCart,
  hideControls,
}: ProductCardProps) => {
  const { t } = useTranslation(['market-shop'])
  const [shouldUseFallback, setShouldUseFallback] = useState(false)

  const isInCart = quantity > 0
  const canAddToCart = product.unlimited ? true : quantity < product.quantity
  const isOutOfStock = product.quantity <= 0 && !product.unlimited
  const discountTag = product.tags.find((tag) => tag.name === 'Discount')
  const newTag = product.tags.find((tag) => tag.name === 'NEW')

  const { isResidentFlow } = useGetSettingsByToken()

  const handleOpenProductDetails = (product: MarketShopProductModel) => {
    popupFactory.open(ProductDetails, { product })
  }

  const handleOpenProductDetailsClick = () => {
    // NOTE: for now, when a product is out of stock, we don't want to allow opening the details
    if (isOutOfStock) return
    handleOpenProductDetails(product)
  }

  const handleSubtractFromCart = () => onSubtractFromCart(product)

  const handleAddToCart = () => {
    if (isResidentFlow && !isInCart) return handleOpenProductDetails(product)
    onAddToCart(product)
  }

  const handleImageError = () => {
    setShouldUseFallback(true)
  }

  useEffect(() => {
    setShouldUseFallback(false)
  }, [product.image])

  return (
    <S.ProductCardContainer $isOutOfStock={isOutOfStock}>
      {isOutOfStock && (
        <S.OutOfStockChip variant="default">
          {t('market-shop:productCard.outOfStock')}
        </S.OutOfStockChip>
      )}
      {(discountTag || newTag) && (
        <S.TagsColumn>
          {discountTag && (
            <Chip variant="red" size="small">
              -{discountTag.discount}%
            </Chip>
          )}
          {newTag && (
            <Chip variant="green" size="small">
              {t('market-shop:productCard.newTag')}
            </Chip>
          )}
        </S.TagsColumn>
      )}
      <S.ImageContainer onClick={handleOpenProductDetailsClick}>
        {product.image && !shouldUseFallback ? (
          <S.Image
            src={getMarketShopImageHref(product.image)}
            alt={product.summary}
            loading="eager"
            onError={handleImageError}
          />
        ) : (
          <S.PlaceholderImage icon={StarFourIcon} size="6.8rem" />
        )}
      </S.ImageContainer>
      <S.Content>
        <S.Title onClick={handleOpenProductDetailsClick}>
          <Typography as="span" color="black800" size="text-large" weight="semibold" breakLines={2}>
            {product.summary}
          </Typography>
        </S.Title>
        <S.Description>
          <Typography as="span" color="black500" size="text-small" breakLines={3}>
            {product.description}
          </Typography>
        </S.Description>
        {!isOutOfStock && (
          <S.Controls>
            <S.PriceGroup>
              {product.discountedPrice !== product.price && (
                <S.OriginalPrice>
                  <Typography as="span" color="black500" size="text-small">
                    {formatPrice(product.price, { zeroPrice: true })}
                  </Typography>
                </S.OriginalPrice>
              )}
              <Typography as="span" color="black800" size="text-small" weight="semibold">
                {formatPrice(
                  product.discountedPrice !== product.price
                    ? product.discountedPrice
                    : product.price,
                  { zeroPrice: true },
                )}
              </Typography>
            </S.PriceGroup>
            {!hideControls && isInCart && (
              <Button onlyBox use="secondary-gray" onClick={handleSubtractFromCart}>
                <Icon icon={MinusIcon} />
              </Button>
            )}
            {!hideControls && isInCart && (
              <S.Count>
                <Typography as="span" color="black800" size="text-small" weight="semibold">
                  {quantity}
                </Typography>
              </S.Count>
            )}
            {!hideControls && (
              <Button onlyBox use="primary" disabled={!canAddToCart} onClick={handleAddToCart}>
                <Icon icon={PlusIcon} />
              </Button>
            )}
          </S.Controls>
        )}
      </S.Content>
    </S.ProductCardContainer>
  )
}
